import React from "react";
import { useTranslation } from "react-i18next";
import {
  Archive,
  ArrowDown,
  ArrowRight,
  CheckCircle2,
  Cloud,
  Database,
  FileDown,
  Files,
  FileType2,
  FolderArchive,
} from "lucide-react";

function FlowArrow() {
  return (
    <div
      className="flex items-center justify-center text-contrast"
      aria-hidden="true"
    >
      <ArrowDown className="h-9 w-9 md:hidden" strokeWidth={1.8} />
      <ArrowRight className="hidden h-12 w-12 md:block" strokeWidth={1.8} />
    </div>
  );
}

function BowArrow({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 60 60"
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <marker
          id="migrator-bow-arrowhead"
          viewBox="0 0 8 8"
          refX="1"
          refY="4"
          markerWidth="13"
          markerHeight="13"
          orient="auto"
          markerUnits="userSpaceOnUse"
          overflow="visible"
        >
          <path d="M0 0 8 4 0 8Z" fill="#A27A5F" />
        </marker>
      </defs>
      <path
        d="M8 38A44 44 0 0 1 52 38"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        markerEnd="url(#migrator-bow-arrowhead)"
      />
    </svg>
  );
}

function Item({ icon: Icon, children, warm = false, bright = false, inverse = false }) {
  return (
    <div className="flex flex-col items-center gap-1 text-center">
      <div
        className={`flex h-20 w-20 items-center justify-center ${bright ? "text-[#fff1e8]" : warm ? "text-contrast" : "text-[#a6b2c2]"}`}
      >
        <Icon className="h-16 w-16" aria-hidden="true" strokeWidth={1.55} />
      </div>
      <span className={`max-w-[12rem] text-sm font-medium leading-snug ${inverse ? "text-white" : "text-main-dark"}`}>
        {children}
      </span>
    </div>
  );
}

function MigratorFlow() {
  const { t } = useTranslation();
  return (
    <div className="w-full py-4 md:py-6">
      <div className="grid items-center gap-7 md:grid-cols-[1.08fr_auto_1.18fr_auto_1.08fr] md:gap-3">
        <div className="flex flex-col justify-center rounded-2xl bg-secondary p-6 md:h-[26rem] md:p-7">
          <h2 className="mb-6 whitespace-pre-line text-center text-xl font-bold leading-tight text-white md:text-2xl">
            {t("pages.migrator.flow.sourceTitle")}
          </h2>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            <Item icon={Archive} inverse>{t("pages.migrator.flow.archive")}</Item>
            <Item icon={FolderArchive} inverse>
              {t("pages.migrator.flow.ondemand")}
            </Item>
            <div className="col-span-2 mx-auto w-1/2 min-w-[8rem]">
              <Item icon={Database} inverse>{t("pages.migrator.flow.database")}</Item>
            </div>
          </div>
        </div>
        <FlowArrow />
        <div className="relative flex flex-col justify-center rounded-2xl bg-[#f8e3d5] p-6 md:h-[28rem] md:p-7">
          <h2 className="relative mb-6 px-3 text-center text-xl font-bold leading-tight text-main-dark md:text-2xl">
            {t("pages.migrator.flow.engineTitle")}
          </h2>
          <div className="relative mx-auto h-[17rem] w-full max-w-[22rem]">
            <div className="absolute left-0 top-0 w-[43%]">
              <Item icon={FileDown} warm>
                {t("pages.migrator.flow.extraction")}
              </Item>
            </div>
            <div className="absolute right-0 top-0 w-[43%]">
              <Item icon={FileType2} warm>
                {t("pages.migrator.flow.conversion")}
              </Item>
            </div>
            <div className="absolute bottom-0 left-1/2 w-[48%] min-w-[8rem] -translate-x-1/2">
              <Item icon={Database} warm>
                {t("pages.migrator.flow.migrationDatabase")}
              </Item>
              <CheckCircle2
                className="absolute left-1/2 top-12 h-5 w-5 translate-x-3 rounded-full bg-[#f8e3d5] text-contrast"
                aria-hidden="true"
                strokeWidth={2.2}
              />
            </div>
            <BowArrow className="absolute left-1/2 -top-2 h-20 w-20 -translate-x-1/2 text-contrast" />
            <BowArrow className="absolute bottom-[3.5rem] right-[8%] h-20 w-20 rotate-[120deg] text-contrast" />
            <BowArrow className="absolute bottom-[3.5rem] left-[8%] h-20 w-20 rotate-[240deg] text-contrast" />
          </div>
        </div>
        <FlowArrow />
        <div className="flex flex-col justify-center rounded-2xl bg-secondary p-6 md:h-[26rem] md:p-7">
          <h2 className="mb-6 whitespace-pre-line text-center text-xl font-bold leading-tight text-white md:text-2xl">
            {t("pages.migrator.flow.targetTitle")}
          </h2>
          <div className="mx-auto grid max-w-[12rem] grid-cols-1 gap-5">
            <Item icon={Cloud} bright inverse>
              {t("pages.migrator.flow.cloud")}
            </Item>
            <Item icon={Files} bright inverse>
              {t("pages.migrator.flow.structure")}
            </Item>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MigratorFlow;
